import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useLanguage } from '../../context/LanguageContext';
import { Ruler, Calculator } from 'lucide-react';

const PrisPerHektarKalkylator = () => {
  const { language } = useLanguage();
  const [kopesumma, setKopesumma] = useState(5000000);
  const [areal, setAreal] = useState(50);
  const [virkesvolym, setVirkesvolym] = useState(7500);
  const [result, setResult] = useState(null);

  const content = {
    sv: {
      title: 'Pris per hektar / m³sk',
      description: 'Beräkna priset per hektar och per kubikmeter stående skog vid ett fastighetsköp. Använd för att jämföra priser mellan olika skogsfastigheter.',
      purchasePrice: 'Köpesumma (kr)',
      area: 'Areal (hektar)',
      volume: 'Virkesvolym (m³sk)',
      calculate: 'Beräkna nyckeltal',
      result: 'Resultat',
      pricePerHa: 'Pris per hektar',
      pricePerM3sk: 'Pris per m³sk',
      volumePerHa: 'Volym per hektar',
      tip: 'Tips: Jämför med regionala genomsnitt för att bedöma om priset är rimligt.'
    },
    de: {
      title: 'Preis pro Hektar / m³',
      description: 'Berechnen Sie den Preis pro Hektar und pro Kubikmeter bei einem Immobilienkauf.',
      purchasePrice: 'Kaufpreis (SEK)',
      area: 'Fläche (Hektar)',
      volume: 'Holzvolumen (m³)',
      calculate: 'Kennzahlen berechnen',
      result: 'Ergebnis',
      pricePerHa: 'Preis pro Hektar',
      pricePerM3sk: 'Preis pro m³',
      volumePerHa: 'Volumen pro Hektar',
      tip: 'Tipp: Vergleichen Sie mit regionalen Durchschnittswerten.'
    },
    en: {
      title: 'Price per hectare / m³',
      description: 'Calculate price per hectare and per cubic meter for a property purchase.',
      purchasePrice: 'Purchase price (SEK)',
      area: 'Area (hectares)',
      volume: 'Timber volume (m³)',
      calculate: 'Calculate metrics',
      result: 'Result',
      pricePerHa: 'Price per hectare',
      pricePerM3sk: 'Price per m³',
      volumePerHa: 'Volume per hectare',
      tip: 'Tip: Compare with regional averages to assess if the price is reasonable.'
    },
    se: {
      title: 'Haddi hektáras / m³',
      description: 'Rehkenas haddi hektáras ja kubihkamehtara opmodatoastimis.',
      purchasePrice: 'Oastinhaddi (SEK)',
      area: 'Areála (hektárat)',
      volume: 'Muorravoluma (m³)',
      calculate: 'Rehkenas',
      result: 'Boađus',
      pricePerHa: 'Haddi hektáras',
      pricePerM3sk: 'Haddi m³',
      volumePerHa: 'Voluma hektáras',
      tip: 'Rávva: Buorit guovlluid gaskamearálaš árvvuiguin.'
    }
  };

  const c = content[language] || content.sv;

  const calculatePrices = () => {
    const pricePerHa = kopesumma / areal;
    const pricePerM3sk = kopesumma / virkesvolym;
    const volumePerHa = virkesvolym / areal;
    
    setResult({
      pricePerHa: Math.round(pricePerHa),
      pricePerM3sk: Math.round(pricePerM3sk),
      volumePerHa: Math.round(volumePerHa)
    });
  };

  return (
    <Card className="w-full" data-testid="pris-per-hektar-calculator">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-forest-700">
          <Ruler className="h-5 w-5" />
          {c.title}
        </CardTitle>
        <CardDescription>{c.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>{c.purchasePrice}</Label>
            <Input
              type="number"
              value={kopesumma}
              onChange={(e) => setKopesumma(Number(e.target.value))}
              min={0}
              step={100000}
              data-testid="price-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label>{c.area}</Label>
            <Input
              type="number"
              value={areal}
              onChange={(e) => setAreal(Number(e.target.value))}
              min={0.1}
              step={0.1}
              data-testid="area-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label>{c.volume}</Label>
            <Input
              type="number"
              value={virkesvolym}
              onChange={(e) => setVirkesvolym(Number(e.target.value))}
              min={1}
              data-testid="volume-input"
            />
          </div>
        </div>

        <Button 
          onClick={calculatePrices} 
          className="w-full bg-forest-700 hover:bg-forest-800"
          data-testid="calculate-btn"
        >
          <Calculator className="mr-2 h-4 w-4" />
          {c.calculate}
        </Button>

        {result && (
          <div className="mt-4 p-4 bg-forest-50 rounded-lg border border-forest-200" data-testid="result">
            <h4 className="font-semibold text-forest-800 mb-3">{c.result}</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">{c.pricePerHa}</p>
                <p className="text-2xl font-bold text-forest-700">{result.pricePerHa.toLocaleString('sv-SE')}</p>
                <p className="text-xs text-muted-foreground">kr/ha</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">{c.pricePerM3sk}</p>
                <p className="text-2xl font-bold text-forest-700">{result.pricePerM3sk.toLocaleString('sv-SE')}</p>
                <p className="text-xs text-muted-foreground">kr/m³sk</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground">{c.volumePerHa}</p>
                <p className="text-2xl font-bold text-forest-600">{result.volumePerHa}</p>
                <p className="text-xs text-muted-foreground">m³sk/ha</p>
              </div>
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground">{c.tip}</p>
      </CardContent>
    </Card>
  );
};

export default PrisPerHektarKalkylator;
